
import { useState, useEffect } from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    description: '',
    contactMethod: 'phone',
    timeframe: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('address', formData.address);
      formBody.append('service', formData.service);
      formBody.append('description', formData.description.slice(0, 500));
      formBody.append('contactMethod', formData.contactMethod);
      formBody.append('timeframe', formData.timeframe);

      const response = await fetch('https://readdy.ai/api/form/d4u285mcd4084g36bq5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! We\'ll get back to you within 24 hours.');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          address: '',
          service: '',
          description: '',
          contactMethod: 'phone',
          timeframe: '',
        });
      } else {
        setSubmitMessage('Something went wrong. Please try again or call us directly.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Check if URL has hash for quote form
    if (window.location.hash === '#quote-form') {
      setTimeout(() => {
        const formElement = document.getElementById('quote-form-section');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-12 bg-[#F5F3EF]">
        <div className="max-w-[1400px] mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-black mb-4">
            Get Your Free Quote
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 max-w-2xl mx-auto">
            Tell us about your project and we'll get back to you within 24 hours
          </p>
        </div>
      </section>

      {/* Main Content Split Layout */}
      <section id="quote-form-section" className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-[55%_45%] gap-8 lg:gap-12">
            {/* Left Column - Quote Request Form */}
            <div className="w-full">
              <form onSubmit={handleSubmit} data-readdy-form id="quote-request-form" className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm">
                <div className="space-y-6">
                  {/* Full Name */}
                  <div>
                    <label htmlFor="fullName" className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 focus:outline-none focus:border-black transition-colors text-sm sm:text-base"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 focus:outline-none focus:border-black transition-colors text-sm sm:text-base"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 focus:outline-none focus:border-black transition-colors text-sm sm:text-base"
                    />
                  </div>

                  {/* Property Address */}
                  <div>
                    <label htmlFor="address" className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                      Property Address *
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 focus:outline-none focus:border-black transition-colors text-sm sm:text-base"
                    />
                  </div>

                  {/* Service Required */}
                  <div>
                    <label htmlFor="service" className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                      Service Required *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 focus:outline-none focus:border-black transition-colors cursor-pointer text-sm sm:text-base"
                    >
                      <option value="">Select a service</option>
                      <option value="General Building">General Building</option>
                      <option value="Kitchen Refurbishment">Kitchen Refurbishment</option>
                      <option value="Bathroom Refurbishment">Bathroom Refurbishment</option>
                      <option value="Plumbing">Plumbing</option>
                      <option value="Electrical">Electrical</option>
                      <option value="Decorating & Painting">Decorating &amp; Painting</option>
                      <option value="Property Maintenance">Property Maintenance</option>
                      <option value="External Works">External Works</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Project Description */}
                  <div>
                    <label htmlFor="description" className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                      Project Description *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 focus:outline-none focus:border-black transition-colors resize-none text-sm sm:text-base"
                      placeholder="Please describe your project in detail..."
                    ></textarea>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      {formData.description.length}/500 characters
                    </p>
                  </div>

                  {/* Preferred Contact Method */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                      Preferred Contact Method *
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="phone"
                          checked={formData.contactMethod === 'phone'}
                          onChange={handleChange}
                          className="cursor-pointer"
                        />
                        <span className="text-gray-700 text-sm sm:text-base">Phone</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="email"
                          checked={formData.contactMethod === 'email'}
                          onChange={handleChange}
                          className="cursor-pointer"
                        />
                        <span className="text-gray-700 text-sm sm:text-base">Email</span>
                      </label>
                    </div>
                  </div>

                  {/* When to Start */}
                  <div>
                    <label htmlFor="timeframe" className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                      When to Start *
                    </label>
                    <select
                      id="timeframe"
                      name="timeframe"
                      value={formData.timeframe}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 focus:outline-none focus:border-black transition-colors cursor-pointer text-sm sm:text-base"
                    >
                      <option value="">Select timeframe</option>
                      <option value="ASAP">ASAP</option>
                      <option value="1-3 months">1-3 months</option>
                      <option value="3-6 months">3-6 months</option>
                      <option value="Planning stage">Planning stage</option>
                    </select>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-black text-white py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                  </button>

                  {/* Privacy Text */}
                  <p className="text-xs sm:text-sm text-gray-500 text-center">
                    We'll never share your details. Your privacy is important to us.
                  </p>

                  {/* Submit Message */}
                  {submitMessage && (
                    <div className={`p-4 rounded-lg text-sm ${submitMessage.includes('Thank') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                      {submitMessage}
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Right Column - Contact Information */}
            <div className="lg:sticky lg:top-32 h-fit space-y-6 w-full">
              {/* Contact Details Card */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-6">Other Ways to Reach Us</h3>
                <div className="space-y-4">
                  <a href="tel:07773467507" className="flex items-center gap-3 text-black hover:text-gray-700 transition-colors cursor-pointer">
                    <i className="ri-phone-fill text-xl sm:text-2xl"></i>
                    <span className="text-xl sm:text-2xl font-bold break-all">07773 467507</span>
                  </a>
                  <a href="mailto:enquiries@minuucontractors.co.uk" className="flex items-center gap-3 text-gray-600 hover:text-black transition-colors cursor-pointer">
                    <i className="ri-mail-line text-lg sm:text-xl"></i>
                    <span className="text-base sm:text-lg break-all">enquiries@minuucontractors.co.uk</span>
                  </a>
                  <div className="flex items-start gap-3 text-gray-600">
                    <i className="ri-time-line text-lg sm:text-xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-black text-sm sm:text-base">Business Hours</p>
                      <p className="text-sm sm:text-base">Monday - Friday: 9am - 5pm</p>
                      <p className="text-sm sm:text-base">Saturday: 9am - 1pm</p>
                      <p className="text-sm sm:text-base">Sunday: Closed</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-gray-600">
                    <i className="ri-map-pin-line text-lg sm:text-xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-black text-sm sm:text-base">Service Area</p>
                      <p className="text-sm sm:text-base">London &amp; Surrounding Areas</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time Badge */}
              <div className="bg-[#F5F3EF] rounded-2xl p-6">
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl sm:text-3xl text-black"></i>
                  <p className="text-gray-700 font-medium text-sm sm:text-base">
                    We typically respond within 24 hours
                  </p>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                  <i className="ri-shield-check-fill text-xl sm:text-2xl text-black"></i>
                  <span className="font-semibold text-black text-sm sm:text-base">Checkatrade Verified</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-shield-star-fill text-xl sm:text-2xl text-black"></i>
                  <span className="font-semibold text-black text-sm sm:text-base">Fully Insured</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-price-tag-3-fill text-xl sm:text-2xl text-black"></i>
                  <span className="font-semibold text-black text-sm sm:text-base">Free Quotes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-lg h-[300px] sm:h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d158857.7281066494!2d-0.24168!3d51.52877!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sLondon%2C%20UK!5e0!3m2!1sen!2s!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="London Service Area"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Alternative Contact Methods */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-black text-center mb-8 sm:mb-12">
            Prefer to Talk?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <a
              href="tel:07773467507"
              className="bg-[#F5F3EF] rounded-2xl p-8 sm:p-10 text-center hover:bg-[#F5F3EF]/80 transition-all cursor-pointer group"
            >
              <i className="ri-phone-fill text-5xl sm:text-6xl text-black mb-4 block"></i>
              <p className="text-2xl sm:text-3xl font-bold text-black mb-2 break-all">07773 467507</p>
              <p className="text-gray-700 group-hover:text-black transition-colors text-sm sm:text-base">Speak to our team</p>
            </a>
            <a
              href="mailto:enquiries@minuucontractors.co.uk"
              className="bg-[#F5F3EF] rounded-2xl p-8 sm:p-10 text-center hover:bg-[#F5F3EF]/80 transition-all cursor-pointer group"
            >
              <i className="ri-mail-fill text-5xl sm:text-6xl text-black mb-4 block"></i>
              <p className="text-lg sm:text-xl font-bold text-black mb-2 break-all">enquiries@minuucontractors.co.uk</p>
              <p className="text-gray-700 group-hover:text-black transition-colors text-sm sm:text-base">Send us a message</p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
